import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiPlay, FiEye, FiStar, FiSearch, FiFilter, FiArrowRight } = FiIcons

const TemplateLibrary = () => {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedTemplate, setSelectedTemplate] = useState(null)

  const categories = [
    { id: 'all', name: 'All Templates', count: 24 },
    { id: 'film', name: 'Film Pitches', count: 8 },
    { id: 'tv', name: 'TV Series', count: 6 },
    { id: 'documentary', name: 'Documentaries', count: 4 },
    { id: 'commercial', name: 'Commercials', count: 3 },
    { id: 'music-video', name: 'Music Videos', count: 3 }
  ]

  useEffect(() => {
    // Simulate loading templates
    setTimeout(() => {
      setTemplates([
        {
          id: 1,
          title: 'Horror Film Pitch',
          category: 'film',
          description: 'Perfect template for horror and thriller film pitches',
          thumbnail: 'https://images.unsplash.com/photo-1520637736862-4d197d17c38a?w=400&h=300&fit=crop',
          slides: 7,
          rating: 4.8,
          uses: 1247,
          tags: ['horror', 'thriller', 'dark', 'atmospheric'],
          isPremium: false
        },
        {
          id: 2,
          title: 'Netflix Series Pitch',
          category: 'tv',
          description: 'Professional template for streaming series presentations',
          thumbnail: 'https://images.unsplash.com/photo-1489599511986-c4d8e5b6b2e6?w=400&h=300&fit=crop',
          slides: 12,
          rating: 4.9,
          uses: 2156,
          tags: ['series', 'netflix', 'streaming', 'professional'],
          isPremium: true
        },
        {
          id: 3,
          title: 'Documentary Proposal',
          category: 'documentary',
          description: 'Compelling format for documentary film proposals',
          thumbnail: 'https://images.unsplash.com/photo-1583212292454-1fe6229603b7?w=400&h=300&fit=crop',
          slides: 9,
          rating: 4.7,
          uses: 892,
          tags: ['documentary', 'educational', 'factual', 'impact'],
          isPremium: false
        },
        {
          id: 4,
          title: 'Sci-Fi Epic Template',
          category: 'film',
          description: 'Futuristic design for science fiction projects',
          thumbnail: 'https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=400&h=300&fit=crop',
          slides: 10,
          rating: 4.6,
          uses: 734,
          tags: ['sci-fi', 'futuristic', 'space', 'technology'],
          isPremium: true
        },
        {
          id: 5,
          title: 'Romantic Comedy Pitch',
          category: 'film',
          description: 'Light and charming template for rom-com pitches',
          thumbnail: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400&h=300&fit=crop',
          slides: 8,
          rating: 4.5,
          uses: 567,
          tags: ['romance', 'comedy', 'light', 'heartwarming'],
          isPremium: false
        },
        {
          id: 6,
          title: 'Commercial Campaign',
          category: 'commercial',
          description: 'Dynamic template for advertising campaigns',
          thumbnail: 'https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?w=400&h=300&fit=crop',
          slides: 6,
          rating: 4.4,
          uses: 445,
          tags: ['commercial', 'advertising', 'brand', 'campaign'],
          isPremium: true
        }
      ])
      setLoading(false)
    }, 1000)
  }, [])

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const handleUseTemplate = (template) => {
    // Create project from template
    const projectData = {
      title: `${template.title} Project`,
      slides: generateTemplateSlides(template),
      createdAt: new Date(),
      updatedAt: new Date(),
      templateId: template.id
    }

    const projectId = Math.random().toString(36).substr(2, 9)
    localStorage.setItem(`nupitch_project_${projectId}`, JSON.stringify(projectData))
    navigate(`/editor/${projectId}`)
  }

  const generateTemplateSlides = (template) => {
    // Generate slides based on template
    return [
      {
        id: 1,
        type: 'title',
        elements: [
          {
            id: 'title-1',
            type: 'text',
            content: 'Project Title',
            style: {
              fontSize: '48px',
              fontWeight: 'bold',
              color: '#ffffff',
              textAlign: 'center',
              position: { x: 50, y: 200 },
              width: 700,
              height: 80
            }
          }
        ],
        background: {
          type: 'image',
          url: template.thumbnail,
          position: 'center',
          size: 'cover'
        }
      }
    ]
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Template Library</h2>
          <p className="text-gray-600">Professional templates to jumpstart your projects</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        <div className="flex space-x-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === category.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <div
            key={template.id}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200"
          >
            {/* Thumbnail */}
            <div className="relative aspect-video">
              <img
                src={template.thumbnail}
                alt={template.title}
                className="w-full h-full object-cover"
              />
              {template.isPremium && (
                <div className="absolute top-3 left-3">
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                    <SafeIcon icon={FiStar} className="w-3 h-3" />
                    <span>Pro</span>
                  </span>
                </div>
              )}
              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 hover:opacity-100">
                <button
                  onClick={() => setSelectedTemplate(template)}
                  className="bg-white text-gray-900 px-4 py-2 rounded-lg font-medium flex items-center space-x-2 shadow-lg"
                >
                  <SafeIcon icon={FiEye} className="w-4 h-4" />
                  <span>Preview</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-gray-900">{template.title}</h3>
                <div className="flex items-center space-x-1 text-sm text-gray-500">
                  <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-500" />
                  <span>{template.rating}</span>
                </div>
              </div>

              <p className="text-gray-600 text-sm mb-4">{template.description}</p>

              {/* Tags */}
              <div className="flex flex-wrap gap-1 mb-4">
                {template.tags.slice(0, 3).map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Stats */}
              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <span>{template.slides} slides</span>
                <span>{template.uses.toLocaleString()} uses</span>
              </div>

              {/* Action Button */}
              <button
                onClick={() => handleUseTemplate(template)}
                className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2"
              >
                <span>Use Template</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiSearch} className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No templates found</h3>
          <p className="text-gray-600">Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* Template Preview Modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-4xl mx-4 max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">{selectedTemplate.title}</h3>
              <button
                onClick={() => setSelectedTemplate(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            
            <div className="aspect-video bg-gray-100 rounded-lg mb-4 flex items-center justify-center">
              <p className="text-gray-500">Template preview would be shown here</p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setSelectedTemplate(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleUseTemplate(selectedTemplate)
                  setSelectedTemplate(null)
                }}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center space-x-2"
              >
                <span>Use Template</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TemplateLibrary